import React, { useEffect, useRef, useState } from 'react';

import DenominatorPicker from './DenominatorPicker';

import BookmarkIcon from 'Assets/icons/bookmark.svg';
import BarChartIcon from 'Assets/icons/chart-bar.svg';
import DonutChartIcon from 'Assets/icons/chart-donut.svg';
import LineChartIcon from 'Assets/icons/chart-line.svg';
import TableChartIcon from 'Assets/icons/chart-table.svg';

/**
 * The builder strip: mode toggle, measure / denominator / dimension / split
 * pills, chart-type toggle and save-view button.
 *
 *   Ratio:     [measure] by [dimension] ÷ [denominator]
 *   Breakdown: [measure] by [dimension] split by [dimension]
 *
 * @ticket IB-644
 */
export default function InsightsBuilder({
    metadata,
    view,
    onChange,
    chartType,
    onChartType,
    onSaveView,
    negativeCapable,
    availableGroups = [],
    filterKeys = [],
    onFilterChange,
}) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [naming, setNaming] = useState(false);
    const [name, setName] = useState('');

    const pickerRef = useRef(null);
    const filterRef = useRef(null);

    // Close the denominator picker / filter dropdowns on an outside click or Esc.
    useEffect(() => {
        if (!pickerOpen && !filterOpen) {
            return undefined;
        }
        const onMouseDown = (e) => {
            if (pickerOpen && pickerRef.current && !pickerRef.current.contains(e.target)) {
                setPickerOpen(false);
            }
            if (filterOpen && filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setPickerOpen(false);
                setFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [pickerOpen, filterOpen]);

    // Same-axis filter (subset the bars). Empty filterKeys = show all; toggling
    // off the first bar materialises the full set minus that one, and re-selecting
    // every bar collapses back to "all".
    const allGroupKeys = availableGroups.map((g) => g.key);
    const groupShown = (key) => filterKeys.length === 0 || filterKeys.includes(key);
    const toggleFilter = (key) => {
        const base = filterKeys.length === 0 ? allGroupKeys : filterKeys;
        let next = base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
        if (next.length === allGroupKeys.length) {
            next = [];
        }
        onFilterChange(next);
    };

    const isRatio = view.mode === 'ratio';

    const measures = isRatio ? metadata.measures.ratio : metadata.measures.breakdown;
    const dimensions = isRatio ? metadata.dimensions.ratio : metadata.dimensions.breakdown;

    const selectedDenominator = (metadata.denominators || []).find((d) => d.key === view.denominator);

    // Dimensions the active breakdown measure cannot be grouped by (e.g.
    // committed/uncommitted have no per-venue/discipline source) — greyed out.
    const measureMeta = measures.find((m) => m.key === view.measure);
    const excludedDimensions = (measureMeta && measureMeta.excludedDimensions) || [];

    // Dimensions where the committed/uncommitted split is sourceable (dept/cc).
    const splitDimensions = metadata.commitmentSplitDimensions || [];
    const splitSupported = splitDimensions.includes(view.dimension);

    // Headcount position-type filter (Paid / Secondee / Volunteer). Shown only
    // for the headcount measure in breakdown mode. No explicit selection = all.
    const headcountTypes = metadata.headcountTypes || [];
    const allHeadcountKeys = headcountTypes.map((t) => t.key);
    const showHeadcountTypes = !isRatio && view.measure === 'headcount' && headcountTypes.length > 0;
    const headcountSelected = Array.isArray(view.headcountTypes) ? view.headcountTypes : allHeadcountKeys;

    const toggleHeadcountType = (key) => {
        const current = Array.isArray(view.headcountTypes) ? view.headcountTypes : allHeadcountKeys;
        let next;
        if (current.includes(key)) {
            next = current.filter((k) => k !== key);
            if (next.length === 0) {
                return; // keep at least one type selected
            }
        } else {
            // Preserve metadata order so the param is stable.
            next = allHeadcountKeys.filter((k) => current.includes(k) || k === key);
        }
        onChange({ ...view, headcountTypes: next });
    };

    const changeMeasure = (measure) => {
        const nextMeta = measures.find((m) => m.key === measure);
        const nextExcluded = (nextMeta && nextMeta.excludedDimensions) || [];

        // If the current dimension is now incompatible, snap to the first allowed one.
        const dimension = nextExcluded.includes(view.dimension) ? dimensions.find((d) => !nextExcluded.includes(d)) || view.dimension : view.dimension;

        // Default the headcount type filter to all when switching to headcount;
        // clear it otherwise so it isn't sent for non-headcount measures.
        const nextHeadcountTypes = measure === 'headcount' ? allHeadcountKeys : null;

        onChange({ ...view, measure, dimension, headcountTypes: nextHeadcountTypes });
    };

    const changeDimension = (dimension) => {
        // Clear an active commitment split if the new dimension cannot source it.
        const split = splitDimensions.includes(dimension) ? view.split : null;
        onChange({ ...view, dimension, split });
    };

    const setMode = (mode) => {
        const nextMeasures = mode === 'ratio' ? metadata.measures.ratio : metadata.measures.breakdown;
        const nextDimensions = mode === 'ratio' ? metadata.dimensions.ratio : metadata.dimensions.breakdown;

        onChange({
            ...view,
            mode,
            measure: nextMeasures[0] ? nextMeasures[0].key : view.measure,
            dimension: nextDimensions[0] || view.dimension,
            split: null,
        });
    };

    // Icon-only chart-type toggle. The label is kept for the tooltip / aria-label.
    const chartButtons = [
        { type: 'bar', label: $t('Bar'), Icon: BarChartIcon },
        { type: 'line', label: $t('Line'), Icon: LineChartIcon },
        { type: 'donut', label: $t('Donut'), Icon: DonutChartIcon },
        { type: 'table', label: $t('Table'), Icon: TableChartIcon },
    ];

    // A human-readable default name mirroring the builder sentence.
    const defaultName = () => {
        const measureLabel = measureMeta ? $t(measureMeta.label) : $t(view.measure);
        const dimensionLabel = $t(view.dimension);
        if (isRatio) {
            const denomLabel = selectedDenominator ? $t(selectedDenominator.label) : $t('denominator');
            return `${measureLabel} ${$t('by')} ${dimensionLabel} ÷ ${denomLabel}`;
        }
        return `${measureLabel} ${$t('by')} ${dimensionLabel}${view.split ? ` (${$t('split')})` : ''}`;
    };

    const openNaming = () => {
        setName(defaultName());
        setNaming(true);
    };

    const confirmSave = () => {
        const trimmed = name.trim();
        if (!trimmed) {
            return;
        }
        onSaveView(trimmed);
        setNaming(false);
        setName('');
    };

    return (
        <div className="tw-bg-charcoal-10 tw-rounded-xl tw-p-3.5 tw-mb-4">
            {/* Mode toggle */}
            <div className="tw-inline-flex tw-bg-white tw-rounded-lg tw-p-1 tw-mb-3.5 tw-border tw-border-charcoal-25">
                {['ratio', 'breakdown'].map((mode) => (
                    <button
                        key={mode}
                        type="button"
                        onClick={() => setMode(mode)}
                        className={[
                            'tw-px-4 tw-py-1.5 tw-rounded-md tw-text-[13px]',
                            view.mode === mode ? 'tw-bg-blue tw-text-white tw-font-medium' : 'tw-text-charcoal-75',
                        ].join(' ')}
                    >
                        {mode === 'ratio' ? $t('Ratio') : $t('Breakdown')}
                    </button>
                ))}
            </div>

            {/* Builder row + actions */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-3">
                {/* Controls (left) */}
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-flex-1">
                    <span className="tw-text-[13px] tw-text-charcoal-75">{$t('Show')}</span>
                    <Select value={view.measure} options={measures.map((m) => ({ value: m.key, label: $t(m.label) }))} onChange={changeMeasure} />
                    <span className="tw-text-[13px] tw-text-charcoal-75">{$t('by')}</span>
                    <Select
                        value={view.dimension}
                        options={dimensions.map((d) => ({
                            value: d,
                            label: $t(d),
                            disabled: excludedDimensions.includes(d),
                        }))}
                        onChange={changeDimension}
                    />

                    {isRatio && (
                        <>
                            <span className="tw-text-charcoal-50 tw-font-medium">÷</span>
                            <div ref={pickerRef} className="tw-relative">
                                <button
                                    type="button"
                                    onClick={() => setPickerOpen((o) => !o)}
                                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-white tw-border tw-border-charcoal-50 tw-rounded-lg tw-px-2.5 tw-py-1.5 tw-text-[13px]"
                                >
                                    {selectedDenominator ? $t(selectedDenominator.label) : $t('Select denominator')}
                                    <span className="tw-text-charcoal-50" aria-hidden="true">
                                        ▾
                                    </span>
                                </button>
                                {pickerOpen && (
                                    <div className="tw-absolute tw-z-20 tw-mt-1">
                                        <DenominatorPicker
                                            denominators={metadata.denominators}
                                            dimension={view.dimension}
                                            selected={view.denominator}
                                            onSelect={(denominator) => {
                                                onChange({ ...view, denominator });
                                                setPickerOpen(false);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {!isRatio && (
                        <>
                            <span className="tw-text-[13px] tw-text-charcoal-75">{$t('split by')}</span>
                            <Select
                                value={view.split || ''}
                                options={[
                                    { value: '', label: $t('None') },
                                    {
                                        value: 'committed_uncommitted',
                                        label: $t('Commitment status'),
                                        disabled: !splitSupported,
                                    },
                                ]}
                                onChange={(split) => onChange({ ...view, split: split || null })}
                            />
                        </>
                    )}

                    {showHeadcountTypes && (
                        <>
                            <span className="tw-text-[13px] tw-text-charcoal-75">{$t('type')}</span>
                            <div className="tw-inline-flex tw-items-center tw-gap-3">
                                {headcountTypes.map((t) => {
                                    const selected = headcountSelected.includes(t.key);
                                    return (
                                        <label
                                            key={t.key}
                                            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-text-charcoal-75 tw-cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => toggleHeadcountType(t.key)}
                                                className="tw-h-4 tw-w-4 tw-rounded tw-border-charcoal-50 tw-text-blue focus:tw-ring-blue"
                                            />
                                            {$t(t.label)}
                                        </label>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {availableGroups.length > 0 && onFilterChange && (
                        <div ref={filterRef} className="tw-relative">
                            <button
                                type="button"
                                onClick={() => setFilterOpen((o) => !o)}
                                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-border tw-border-dashed tw-border-charcoal-50 tw-text-charcoal-75 tw-rounded-lg tw-px-2.5 tw-py-1.5 tw-text-[12.5px]"
                            >
                                <span aria-hidden="true">+</span>
                                {filterKeys.length > 0 ? $t('Filter ({{count}})', { count: filterKeys.length }) : $t('Add filter')}
                            </button>
                            {filterOpen && (
                                <div className="tw-absolute tw-z-20 tw-mt-1 tw-bg-white tw-border tw-border-charcoal-25 tw-rounded-lg tw-shadow-lg tw-p-1.5 tw-w-64 tw-max-h-72 tw-overflow-auto">
                                    <div className="tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-1">
                                        <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                                            {$t('Show')} {$t(view.dimension)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => onFilterChange([])}
                                            disabled={filterKeys.length === 0}
                                            className="tw-text-[11px] tw-text-blue disabled:tw-text-charcoal-25"
                                        >
                                            {$t('All')}
                                        </button>
                                    </div>
                                    {availableGroups.map((g) => (
                                        <label
                                            key={g.key}
                                            className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-text-[13px] tw-rounded-md tw-cursor-pointer hover:tw-bg-charcoal-10"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={groupShown(g.key)}
                                                onChange={() => toggleFilter(g.key)}
                                                className="tw-h-4 tw-w-4 tw-rounded tw-border-charcoal-50 tw-text-blue focus:tw-ring-blue"
                                            />
                                            <span className="tw-truncate">{g.label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Chart-type toggle + save view (right) */}
                <div className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-inline-flex tw-gap-0.5 tw-bg-white tw-border tw-border-charcoal-25 tw-rounded-lg tw-p-1">
                        {chartButtons.map((btn) => {
                            const disabled = btn.type === 'donut' && negativeCapable;
                            const active = chartType === btn.type;
                            const { Icon } = btn;
                            return (
                                <button
                                    key={btn.type}
                                    type="button"
                                    disabled={disabled}
                                    aria-label={btn.label}
                                    aria-pressed={active}
                                    title={disabled ? $t('Unavailable for measures that can be negative') : btn.label}
                                    onClick={() => !disabled && onChartType(btn.type)}
                                    className={[
                                        'tw-inline-flex tw-items-center tw-justify-center tw-p-1.5 tw-rounded-md',
                                        active ? 'tw-bg-blue tw-text-white' : 'tw-text-charcoal-50',
                                        disabled ? 'tw-opacity-40 tw-cursor-not-allowed' : '',
                                    ].join(' ')}
                                >
                                    <Icon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                </button>
                            );
                        })}
                    </div>

                    {naming ? (
                        <div className="tw-inline-flex tw-items-center tw-gap-1.5">
                            <input
                                type="text"
                                value={name}
                                autoFocus
                                onChange={(e) => setName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        confirmSave();
                                    } else if (e.key === 'Escape') {
                                        setNaming(false);
                                    }
                                }}
                                placeholder={$t('Name this view')}
                                aria-label={$t('Name this view')}
                                className="tw-bg-white tw-border tw-border-charcoal-50 tw-rounded-lg tw-px-2.5 tw-py-1.5 tw-text-[13px] tw-text-charcoal tw-w-56"
                            />
                            <button
                                type="button"
                                onClick={confirmSave}
                                disabled={!name.trim()}
                                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-green tw-text-[#173404] tw-rounded-lg tw-px-3.5 tw-py-1.5 tw-text-[13px] tw-font-medium disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                            >
                                <BookmarkIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                                {$t('Save')}
                            </button>
                            <button type="button" onClick={() => setNaming(false)} className="tw-text-[13px] tw-text-charcoal-75 tw-px-2 tw-py-1.5">
                                {$t('Cancel')}
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={openNaming}
                            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-bg-green tw-text-[#173404] tw-rounded-lg tw-px-3.5 tw-py-1.5 tw-text-[13px] tw-font-medium"
                        >
                            <BookmarkIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                            {$t('Save view')}
                        </button>
                    )}
                </div>
            </div>

            {isRatio && (
                <p className="tw-mt-2.5 tw-text-[12px] tw-text-charcoal-50">
                    {$t('Reads in calculation order: Total budget by venue ÷ forecast number of tickets sold — denominator label matches the Ticketing model.')}
                </p>
            )}
        </div>
    );
}

function Select({ value, options, onChange }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="tw-inline-flex tw-items-center tw-bg-white tw-border tw-border-charcoal-50 tw-rounded-lg tw-px-2.5 tw-py-1.5 tw-text-[13px] tw-text-charcoal"
        >
            {options.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                </option>
            ))}
        </select>
    );
}
